/**
 * Service Worker Cleanup Utility
 * 
 * Unregisters all service workers and clears caches to prevent stale JavaScript chunks
 * from causing React dispatcher corruption.
 * 
 * This prevents "Cannot read properties of null (reading 'useState/useRef/useContext')"
 * errors in lazy-loaded components caused by cached chunks from previous builds.
 * 
 * In development: Always cleans up to prevent any caching issues.
 * In production: Cleans up on version mismatch to prevent stale chunk errors.
 */

const APP_VERSION = '1.0.1'; // Update this when deploying new versions
const VERSION_KEY = 'app_version';

export async function cleanupStaleServiceWorkers(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) return false;
  
  // Check if this is a new version in production
  const storedVersion = localStorage.getItem(VERSION_KEY);
  const isNewVersion = storedVersion !== APP_VERSION;
  
  // TEMPORARILY disable cleanup in dev mode to isolate the issue
  // Clean up ONLY if version changed in production
  const shouldCleanup = import.meta.env.DEV ? false : isNewVersion;
  
  if (!shouldCleanup) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const cacheNames = await caches.keys();
    
    if (registrations.length > 0 || cacheNames.length > 0) {
      const mode = import.meta.env.DEV ? 'Dev' : 'Production';
      const reason = isNewVersion ? 'version change detected' : 'development mode';
      console.log(`[${mode}] Cleaning up stale service workers and caches (${reason})...`);
      
      // Unregister all service workers
      await Promise.all(registrations.map(r => r.unregister()));
      
      // Clear all caches (workbox precache, runtime cache, React Query cache, etc.)
      await Promise.all(cacheNames.map(name => caches.delete(name)));
      
      // Clear ALL React Query persisted cache variants from localStorage
      const clearedKeys: string[] = [];
      Object.keys(localStorage)
        .filter(key => key.startsWith('REACT_QUERY_OFFLINE_CACHE'))
        .forEach(key => {
          localStorage.removeItem(key);
          clearedKeys.push(key);
        });
      
      if (clearedKeys.length > 0) {
        console.log(`[${mode}] React Query cache cleared from localStorage:`, clearedKeys);
      }
      
      // Update stored version
      localStorage.setItem(VERSION_KEY, APP_VERSION);
      
      console.log(`[${mode}] Cleanup complete. Reloading to ensure clean state...`);
      window.location.reload();
      return true; // Indicates reload was triggered
    }
    
    // No cleanup needed, but update version if it changed
    if (isNewVersion) {
      localStorage.setItem(VERSION_KEY, APP_VERSION);
    }
  } catch (error) {
    console.error('[Cleanup] Service worker cleanup error:', error);
  }
  
  return false; // No cleanup needed or error occurred
}
